import {Card, CardHeader, CardContent, CardActions, Typography, CardProps, useTheme, Snackbar, Alert} from "@mui/material";
import { styled } from "@mui/system";
import React, { useRef, useMemo, useState, forwardRef } from "react";
import { createPortal } from "react-dom";
import {Skeleton} from "@mui/material";
import {getNeonColor} from "../theme";
import {useThemeContext} from "../themeContext";
import html2canvas from "html2canvas";


// Function to generate a stable random position per card instance based on DOM id
const generateBlurPosition = (seed: string) => {
  const hash = seed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const randomFactor = Math.random() * 50; // Add randomness for uniqueness

  return {
    top: `${(hash % 50) + randomFactor}px`, // Random but bounded
    left: `${(hash % 50) + randomFactor}px`,
  };
};


// 🔹 Styled Neon Card (Inner Glow Effect & Depth)
const NeonCardBase = forwardRef<HTMLDivElement, CardProps & {
  id?: string;
  zoom_on_hover?: boolean;
  background_glow?: boolean;
  glow_color?: string;
  screenshotEnabled?: boolean;
}>(({ id, zoom_on_hover, background_glow, glow_color, screenshotEnabled = false, ...props }, ref) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const stableId = useMemo(() => id || Math.random().toString(36).substr(2, 9), [id]);

  // Ensure each card has a slightly different blur position
  const blurPosition = useMemo(() => generateBlurPosition(stableId + Math.random().toString()), [stableId]);
  const theme = useTheme();

  const neonColor = glow_color || getNeonColor(theme); // Use provided color or fallback
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const handleToastClose = () => setToast(prev => ({ ...prev, open: false }));

  const handleScreenshot = async () => {
    const currentRef = ref || internalRef;
    if (!screenshotEnabled || !currentRef || typeof currentRef === 'function' || !currentRef.current) return;

    try {
      // Temporarily hide the neon glow effect
      const glowElement = currentRef.current.querySelector('.neon-glow') as HTMLElement;
      const originalDisplay = glowElement?.style.display;
      if (glowElement) {
        glowElement.style.display = 'none';
      }

             const canvas = await html2canvas(currentRef.current, {
         logging: false,
         useCORS: true,
         allowTaint: true,
         backgroundColor: '#1a1a1a',
       });

      // Restore the neon glow effect
      if (glowElement) {
        glowElement.style.display = originalDisplay || '';
      }

      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            await navigator.clipboard.write([
              // @ts-ignore
              new ClipboardItem({ 'image/png': blob })
            ]);
            setToast({
              open: true,
              message: "Screenshot copied to clipboard",
              severity: "success",
            });
          } catch (err) {
            console.error("Clipboard error:", err);
            setToast({
              open: true,
              message: "Failed to copy to clipboard",
              severity: "error",
            });
          }
        }
      }, 'image/png');
    } catch (err) {
      console.error("Screenshot error:", err);
      setToast({
        open: true,
        message: "Screenshot failed",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Card
          ref={ref || internalRef}
          id={stableId}
          sx={{
            boxShadow: background_glow
                ? `0px 0px 7px ${neonColor}`
                : `0px 0px 30px rgba(25, 25, 35, 0.95)`,
            cursor: screenshotEnabled ? "pointer" : "default",
            transition: screenshotEnabled ? "all 0.2s ease-in-out" : "none",
            "&:hover": zoom_on_hover ? {
              transform: "scale(1.03)",
              transition: "transform 0.3s ease-in-out",
              cursor: "pointer",
            } : {},
          }}
          onClick={screenshotEnabled ? handleScreenshot : undefined}
          {...props}
      >
        <div className="neon-glow" style={{ top: blurPosition.top, left: blurPosition.left, background: neonColor }} />
        {props.children}
      </Card>
      {toast.open && createPortal(
        <Snackbar
          open={toast.open}
          autoHideDuration={3000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{
            position: "fixed",
            zIndex: 9999,
          }}
        >
          <Alert onClose={handleToastClose} severity={toast.severity} variant="filled">
            {toast.message}
          </Alert>
        </Snackbar>,
        document.body
      )}
    </>
  );
});

export const NeonCard = styled(NeonCardBase)(({ theme, glow_color }: { theme: any; glow_color?: string }) => {
  const neonColor = glow_color || getNeonColor(theme); // Ensure glow_color is used

  function getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const pulsate_duration = getRandomInt(2, 5)
  const pulsate_duration2 = getRandomInt(2, 5)

  return {
    // width: "250px",
    // height: "240px",
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    background: theme.palette.background.paper,
    opacity: 1,

    "& .neon-glow": {
      position: "absolute",
      width: "250px",
      height: "250px",
      background: neonColor, // ✅ Now correctly injected
      opacity: 0.08,
      filter: "blur(50px)",
      borderRadius: "50%",
      pointerEvents: "none",
      zIndex: 0,
      /* animation: `pulsate_background_blur ${pulsate_duration2}s infinite alternate` */
    },

    "& > *:not(.neon-glow)": {
      position: "relative",
      zIndex: 1,
    },

    "&::after": {
      content: "''",
      position: "absolute",
      bottom: "0px",
      left: "0px",
      width: "100%",
      transform: glow_color ? "brightness(300%)" : "none",
      height: glow_color ? "3px" : "1px",
      background: neonColor, // ✅ Now correctly injected
      opacity: 1,
      pointerEvents: "none",
      boxShadow: `0px 0px 10px ${neonColor}, 0px 0px 20px ${neonColor}`,
      // animation: `small_pulsate ${1}s infinite alternate`,
      // '--glow-color':  neonColor
    },
  };
});

// 🔹 Styled Neon Card Header
export const NeonCardHeader = styled(CardHeader)(({ theme }) => ({
  minHeight: "50px",
  padding: "12px 16px",
  marginBottom: 0,
  // color: getNeonColor(theme), // Use primary neon color
  position: "relative",
  zIndex: 10, // Stay above scrolling content
  // backgroundColor: theme.palette.background.paper, // Ensure solid background
  "& .MuiTypography-root": {
    fontWeight: "bold",
    textShadow: '0 0 2px #fff',
  },
}));







// 🔹 Styled Neon Card Content
export const NeonCardContent = styled(CardContent)({
  width: "100%",
  padding: "16px",
  "&:last-child": {
    paddingBottom: "16px"
  }
});

// 🔹 Styled Neon Card Footer (Actions)
export const NeonCardActions = styled(CardActions)({
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
  padding: "8px 16px",
  minHeight: "40px"
});

interface NeonMainStatCardProps extends CardProps {
  background?: string;
}

export const NeonMainStatCard = styled(Card)<NeonMainStatCardProps>(({ theme, background }) => {
  const ref = useRef<HTMLDivElement>(null);
  const stableId = useMemo(() => Math.random().toString(36).substr(2, 9), []);
  const blurPosition = useMemo(() => generateBlurPosition(stableId), [stableId]);

  return {
    // width: "300px",
    // height: "125px",
    background: background || "rgba(20, 20, 30, 0.85)", // Use provided background or default
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    backdropFilter: "blur(5px)", // Softens the background
    boxShadow: `
      0px 10px 30px rgba(0, 0, 0, 0.6), 
      0px 4px 15px rgba(0, 0, 0, 0.4), 
      inset 0px 0px 10px rgba(0, 0, 0, 0.3)
    `, // Added depth effect
    "&::before": {
      content: "''",
      position: "absolute",
      top: blurPosition.top,
      left: blurPosition.left,
      width: "200px",
      height: "200px",
      background: getNeonColor(theme),
      opacity: 0.08, // Light blur effect
      filter: "blur(50px)",
      borderRadius: "50%",
      pointerEvents: "none",
    },
    "&::after": {
      content: "''",
      position: "absolute",
      bottom: "0px",
      left: "0px",
      width: "100%",
      height: "2px",
      background: getNeonColor(theme),
      boxShadow: `0px 0px 10px ${getNeonColor(theme)}`,
      opacity: 1,
      pointerEvents: "none",
    },
  };
});

export function NeonSkeletonCard(props: {sx?: any}){
  return <Card sx={props.sx ? props.sx :  {width:'250px', minHeight:'120px'}}>
                    <CardHeader sx={{minHeight:'50px', padding:'12px 16px', mb:0}} title={'Loading...'}/>
                    <CardContent sx={{width:'100%', padding:'8px', alignItems:'center', justifyContent:'center', display:'flex', mt:0}}>
                        <Skeleton variant="rectangular" width={'100%'} height={'60px'} />
                    </CardContent>
                </Card>
}